import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { SlideOver } from "../components/shared/SlideOver";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { useAdminT } from "../../../hooks/useAdminT";

export default function PropertyTypesPage() {
    const { t } = useAdminT();
    const queryClient = useQueryClient();
    const [isDrawOpen, setIsDrawOpen] = useState(false);
    const [editingType, setEditingType] = useState<any>(null);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);

    const { register, handleSubmit, reset, setValue } = useForm();

    const { data: types, isLoading } = useQuery({
        queryKey: ["property-types"],
        queryFn: () => api.get("/api/admin/property-types").then(res => res.data.data)
    });

    const createMutation = useMutation({
        mutationFn: (data: any) => api.post("/api/admin/property-types", data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["property-types"] });
            handleClose();
        }
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/property-types/${editingType.id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["property-types"] });
            handleClose();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/property-types/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["property-types"] });
            setDeleteTarget(null);
        }
    });

    const handleClose = () => {
        setIsDrawOpen(false);
        setEditingType(null);
        reset();
    };

    const handleEdit = (item: any) => {
        setEditingType(item);
        setValue("slug", item.slug);
        setValue("icon", item.icon);
        setValue("order", item.order);
        setValue("nameEn", item.name?.en || item.slug);
        setValue("nameAr", item.name?.ar || "");
        setIsDrawOpen(true);
    };

    const onSubmit = (data: any) => {
        const payload = {
            slug: data.slug,
            icon: data.icon,
            order: Number(data.order),
            translations: [
                { locale: "en", name: data.nameEn },
                { locale: "ar", name: data.nameAr }
            ]
        };

        if (editingType) {
            updateMutation.mutate(payload);
        } else {
            createMutation.mutate(payload);
        }
    };

    return (
        <div className="p-8">
            <PageHeader
                title={t("propertyTypes.title")}
                description={t("propertyTypes.subtitle")}
                actions={[
                    {
                        label: t("propertyTypes.add"),
                        icon: Plus,
                        onClick: () => setIsDrawOpen(true)
                    }
                ]}
            />

            <AdminTable
                data={types}
                isLoading={isLoading}
                columns={[
                    { header: t("banners.fields.order"), cell: (t) => t.order, enableSorting: true },
                    { header: t("common.slug"), cell: (t) => t.slug },
                    { header: t("common.nameEn"), cell: (t) => t.name?.en || "-" },
                    { header: t("common.nameAr"), cell: (t) => t.name?.ar || "-" },
                    { header: t("propertyTypes.fields.icon"), cell: (t) => t.icon || "-" },
                ]}
                onEdit={handleEdit}
                onDelete={(t) => setDeleteTarget(t)}
            />

            <ConfirmDialog
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                title={t("propertyTypes.delete")}
                message={`${t("common.confirmProceed")} ${deleteTarget?.slug || ""}`}
                confirmText={t("common.delete")}
                isLoading={deleteMutation.isPending}
            />

            <SlideOver
                isOpen={isDrawOpen}
                onClose={handleClose}
                title={editingType ? t("propertyTypes.edit") : t("propertyTypes.add")}
                footer={
                    <>
                        <button onClick={handleClose} className="px-4 py-2 border rounded hover:bg-slate-50">{t("common.cancel")}</button>
                        <button onClick={handleSubmit(onSubmit)} className="px-4 py-2 bg-secondary text-white rounded hover:bg-primary-dark">
                            {editingType ? t("common.save") : t("common.create")}
                        </button>
                    </>
                }
            >
                <form className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium">{t("common.slug")}</label>
                        <input {...register("slug", { required: true })} disabled={!!editingType} className="mt-1 w-full px-3 py-2 border rounded-md disabled:bg-slate-100" placeholder={t("propertyTypes.placeholders.slug")} />
                        <p className="text-xs text-slate-500 mt-1">{t("propertyTypes.slugHint")}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium">{t("banners.fields.order")}</label>
                        <input type="number" {...register("order")} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder="0" />
                    </div>

                    <div>
                        <label className="block text-sm font-medium">{t("propertyTypes.fields.icon")}</label>
                        <input {...register("icon")} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder={t("propertyTypes.placeholders.icon")} />
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                        <h3 className="font-medium text-slate-800 mb-3">{t("common.locale.en")} / {t("common.locale.ar")}</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">{t("common.nameEn")}</label>
                                <input {...register("nameEn", { required: true })} className="mt-1 w-full px-3 py-2 border rounded-md bg-white" placeholder={t("propertyTypes.placeholders.nameEn")} />
                            </div>
                            <div dir="rtl">
                                <label className="block text-xs font-medium uppercase text-slate-500">{t("common.nameAr")}</label>
                                <input {...register("nameAr", { required: true })} className="mt-1 w-full px-3 py-2 border rounded-md bg-white" placeholder={t("propertyTypes.placeholders.nameAr")} />
                            </div>
                        </div>
                    </div>
                </form>
            </SlideOver>
        </div>
    );
}
